import React, { useState, useEffect } from 'react';
import { 
  Check, 
  X, 
  Edit3, 
  Trash2, 
  BookOpen, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Plus,
  Calendar,
  Hash
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from './LoadingButton';

export interface ScannedBook {
  id: string;
  title: string;
  author: string;
  enriched: boolean;
  enrichmentStatus: 'pending' | 'loading' | 'success' | 'failed';
  googleData?: {
    google_id: string;
    isbn: string;
    published_date: string;
    description: string;
    cover_image: string;
  };
  selected: boolean;
}

interface ScannedBooksListProps {
  books: ScannedBook[];
  onBooksChange: (books: ScannedBook[]) => void;
  onEditBook: (book: ScannedBook) => void;
  onAddToLibrary: (selectedBooks: ScannedBook[]) => void;
}

const ScannedBooksList: React.FC<ScannedBooksListProps> = ({
  books,
  onBooksChange,
  onEditBook,
  onAddToLibrary
}) => {
  const { showError } = useToast();
  const { isLoading, withLoading } = useLoading();
  const [enrichingBooks, setEnrichingBooks] = useState<Set<string>>(new Set());

  // Auto-enrich books when they are first added
  useEffect(() => {
    const pendingBooks = books.filter(book => 
      book.enrichmentStatus === 'pending' && !enrichingBooks.has(book.id)
    );

    if (pendingBooks.length > 0) {
      enrichBooks(pendingBooks);
    }
  }, [books, enrichingBooks]);

  // Enrich books with Google Books API
  const enrichBooks = async (booksToEnrich: ScannedBook[]) => {
    const newEnrichingBooks = new Set(enrichingBooks);
    booksToEnrich.forEach(book => newEnrichingBooks.add(book.id));
    setEnrichingBooks(newEnrichingBooks);

    // Update books to loading status
    const updatedBooks = books.map(book => 
      booksToEnrich.some(b => b.id === book.id)
        ? { ...book, enrichmentStatus: 'loading' as const }
        : book
    );
    onBooksChange(updatedBooks);

    // Enrich each book
    const enrichedBooks = await Promise.all(
      booksToEnrich.map(async (book) => {
        try {
          const response = await fetch('/api/books/enrich-from-google', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              title: book.title,
              author: book.author
            })
          });

          if (!response.ok) {
            throw new Error('Enrichment failed');
          }

          const data = await response.json();
          
          return {
            ...book,
            enriched: data.success,
            enrichmentStatus: data.success ? 'success' as const : 'failed' as const,
            googleData: data.success ? data.data : undefined
          };
        } catch (error) {
          console.error('Error enriching book:', error);
          return {
            ...book,
            enriched: false,
            enrichmentStatus: 'failed' as const
          };
        }
      })
    );

    // Update books with enriched data
    const finalBooks = books.map(book => {
      const enrichedBook = enrichedBooks.find(b => b.id === book.id);
      return enrichedBook || book;
    });

    onBooksChange(finalBooks);

    // Remove from enriching set
    const newEnrichingBooksAfter = new Set(enrichingBooks);
    booksToEnrich.forEach(book => newEnrichingBooksAfter.delete(book.id));
    setEnrichingBooks(newEnrichingBooksAfter);
  };

  // Toggle book selection
  const toggleBookSelection = (bookId: string) => {
    const updatedBooks = books.map(book =>
      book.id === bookId ? { ...book, selected: !book.selected } : book
    );
    onBooksChange(updatedBooks);
  };

  // Toggle all books selection
  const toggleAllSelection = () => {
    const allSelected = books.every(book => book.selected);
    const updatedBooks = books.map(book => ({ ...book, selected: !allSelected }));
    onBooksChange(updatedBooks);
  };

  // Remove book from list
  const removeBook = (bookId: string) => {
    const updatedBooks = books.filter(book => book.id !== bookId);
    onBooksChange(updatedBooks);
  };

  // Get status icon and text
  const getStatusInfo = (book: ScannedBook) => {
    switch (book.enrichmentStatus) {
      case 'loading':
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin text-forest-700" />,
          text: 'Enrichissement...',
          className: 'text-forest-700'
        };
      case 'success':
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          text: 'Enrichi ✓',
          className: 'text-green-600'
        };
      case 'failed':
        return {
          icon: <AlertCircle className="h-4 w-4 text-orange-600" />,
          text: 'Non trouvé',
          className: 'text-orange-600'
        };
      default:
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin text-gray-400" />,
          text: 'En attente...',
          className: 'text-gray-400'
        };
    }
  };

  // Format publication date
  const formatPublicationDate = (dateString: string) => {
    if (!dateString || dateString === 'N/A') return 'Date inconnue';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date inconnue';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  const selectedBooks = books.filter(book => book.selected);
  const allSelected = books.length > 0 && books.every(book => book.selected);
  const someSelected = books.some(book => book.selected);

  if (books.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with selection controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-forest-700" />
            Livres détectés ({books.length})
          </h3>
          
          {books.length > 0 && (
            <div className="flex items-center space-x-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAllSelection}
                  className="h-4 w-4 text-forest-700 border-gray-300 rounded focus:ring-forest-600"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </span>
              </label>
            </div>
          )}
        </div>

        {selectedBooks.length > 0 && (
          <div className="bg-forest-50 border border-forest-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-forest-700 mr-2" />
                <span className="text-forest-800 font-medium">
                  {selectedBooks.length} livre{selectedBooks.length > 1 ? 's' : ''} sélectionné{selectedBooks.length > 1 ? 's' : ''}
                </span>
              </div>
              
              <LoadingButton
                onClick={() => onAddToLibrary(selectedBooks)}
                loading={isLoading('add-batch-books')}
                loadingText="Ajout en cours..."
                className="px-4 py-2"
                variant="primary"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter à ma bibliothèque
              </LoadingButton>
            </div>
          </div>
        )}
      </div>

      {/* Books list */}
      <div className="space-y-3">
        {books.map((book) => {
          const statusInfo = getStatusInfo(book);
          
          return (
            <div
              key={book.id}
              className={`bg-white rounded-lg border transition-all duration-200 ${
                book.selected
                  ? 'border-forest-300 bg-forest-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start space-x-4">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 pt-1">
                    <input
                      type="checkbox"
                      checked={book.selected}
                      onChange={() => toggleBookSelection(book.id)}
                      className="h-4 w-4 text-forest-700 border-gray-300 rounded focus:ring-forest-600"
                    />
                  </div>

                  {/* Book cover */}
                  <div className="flex-shrink-0">
                    {book.googleData?.cover_image ? (
                      <img
                        src={book.googleData.cover_image}
                        alt={book.title}
                        className="w-16 h-20 object-cover rounded-lg shadow-sm"
                      />
                    ) : (
                      <div className="w-16 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Book details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-gray-900 line-clamp-2 mb-1">
                          {book.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-2">
                          par <span className="font-medium">{book.author}</span>
                        </p>

                        {/* Enriched data */}
                        {book.enriched && book.googleData && (
                          <div className="space-y-1 text-xs text-gray-500">
                            {book.googleData.isbn && book.googleData.isbn !== 'N/A' && (
                              <div className="flex items-center">
                                <Hash className="h-3 w-3 mr-1" />
                                <span>ISBN: {book.googleData.isbn}</span>
                              </div>
                            )}
                            {book.googleData.published_date && book.googleData.published_date !== 'N/A' && (
                              <div className="flex items-center">
                                <Calendar className="h-3 w-3 mr-1" />
                                <span>{formatPublicationDate(book.googleData.published_date)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Status and actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Status */}
                        <div className={`flex items-center ${statusInfo.className}`}>
                          {statusInfo.icon}
                          <span className="ml-1 text-xs font-medium">
                            {statusInfo.text}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => onEditBook(book)}
                            className="p-1 text-gray-400 hover:text-forest-700 transition-colors"
                            title="Modifier"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => removeBook(book.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {books.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucun livre détecté
          </h3>
          <p className="text-gray-600">
            Essayez de prendre des photos plus claires des couvertures de livres
          </p>
        </div>
      )}
    </div>
  );
};

export default ScannedBooksList;
